// Dialers.jsx - Comprehensive Dialers Dashboard
// v3.93 - Corporate styling matching Machines page

import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus, Search, Filter, MoreVertical, Edit, Copy, Trash2,
  Archive, ArchiveRestore, ChevronRight, ChevronLeft, X, Check,
  Building2, Calendar, CheckSquare, AlertTriangle, RefreshCw,
  ExternalLink, Bot, Play, Pause, AlertCircle, Clock, Phone,
  WifiOff, Activity, TrendingUp, ArrowRight, Zap, CheckCircle, Users
} from 'lucide-react'
import { dialerApi, monitoringApi } from '../services/api'
import './Dialers.css'

const PAGE_SIZES = [25, 50, 100, 200]

function Dialers() {
  const navigate = useNavigate()

  // Data states
  const [dialers, setDialers] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Bot summary state
  const [botSummary, setBotSummary] = useState({
    totals: { total: 0, active: 0, paused: 0, waiting: 0, crashed: 0, offline: 0, inactive_3d: 0 },
    dialers: [],
    issues: [],
    offlineBots: []
  })

  // Filter states
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Selection states
  const [selectedDialers, setSelectedDialers] = useState([])
  const [selectAll, setSelectAll] = useState(false)

  // Menu & Modal states
  const [activeMenu, setActiveMenu] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [currentDialer, setCurrentDialer] = useState(null)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [formData, setFormData] = useState({ name: '', is_archived: false })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)

    try {
      const [dialersRes, summaryRes] = await Promise.all([
        dialerApi.getAll(),
        monitoringApi.getDialersSummary().catch(() => ({ data: null }))
      ])

      setDialers(Array.isArray(dialersRes.data) ? dialersRes.data : [])

      if (summaryRes.data) {
        // Calculate offline bots from issues or separate query
        const offlineCount = summaryRes.data.totals?.total -
          (summaryRes.data.totals?.active || 0) -
          (summaryRes.data.totals?.paused || 0) -
          (summaryRes.data.totals?.waiting || 0) -
          (summaryRes.data.totals?.crashed || 0)

        setBotSummary({
          totals: {
            ...summaryRes.data.totals,
            offline: Math.max(0, offlineCount)
          },
          dialers: summaryRes.data.dialers || [],
          issues: summaryRes.data.issues || [],
          offlineBots: summaryRes.data.issues?.filter(i => i.issue === 'offline') || []
        })
      }
    } catch (error) {
      console.error('Failed to load data:', error)
      setDialers([])
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Get bot counts for a specific dialer
  const getBotCounts = (dialerId) => {
    const dialerData = botSummary.dialers.find(d => d.id === dialerId)
    return dialerData || { total: 0, active: 0, paused: 0, waiting: 0, crashed: 0, offline: 0, linked_vms: 0, last_heartbeat: null, client_name: null, avatar_name: null }
  }

  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return '-'
    const diff = Math.abs(Math.floor((new Date() - new Date(date)) / 1000))
    if (diff < 60) return `${diff}s ago`
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return `${Math.floor(diff / 86400)}d ago`
  }

  // Group offline bots by dialer
  const offlineBotsByDialer = useMemo(() => {
    const grouped = {}
    botSummary.issues.filter(b => b.issue === 'offline' || b.issue === 'crashed').forEach(bot => {
      const dialerName = bot.dialer_name || 'Unknown'
      if (!grouped[dialerName]) grouped[dialerName] = []
      grouped[dialerName].push(bot)
    })
    return grouped
  }, [botSummary.issues])

  // Crashed bots (issues that aren't just offline)
  const crashedBots = useMemo(() => {
    return botSummary.issues.filter(b => b.issue === 'crashed' || b.issue === 'restarting')
  }, [botSummary.issues])

  // Filter dialers - search by ID, Name, Client Name
  const filteredDialers = useMemo(() => {
    return dialers.filter(dialer => {
      if (showArchived ? !dialer.is_archived : dialer.is_archived) return false
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const dialerData = botSummary.dialers.find(d => d.id === dialer.id)
        const clientName = dialerData?.client_name?.toLowerCase() || ''
        const dialerId = String(dialer.id)
        const dialerName = dialer.name?.toLowerCase() || ''
        if (!dialerName.includes(query) && !clientName.includes(query) && !dialerId.includes(query)) {
          return false
        }
      }
      return true
    })
  }, [dialers, searchQuery, showArchived, botSummary.dialers])

  // Dialers with bots (sorted by active bots)
  const dialersWithBots = useMemo(() => {
    return botSummary.dialers
      .filter(d => d.total > 0)
      .sort((a, b) => (b.active || 0) - (a.active || 0))
  }, [botSummary.dialers])

  // Pagination
  const totalPages = Math.ceil(filteredDialers.length / pageSize)
  const paginatedDialers = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredDialers.slice(start, start + pageSize)
  }, [filteredDialers, currentPage, pageSize])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, showArchived, pageSize])

  // Toast state
  const [toast, setToast] = useState(null)
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDialers([])
      setSelectAll(false)
    } else {
      setSelectedDialers(paginatedDialers.map(d => d.id))
      setSelectAll(true)
    }
  }

  const handleSelect = (id) => {
    setSelectedDialers(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
    setSelectAll(false)
  }

  // Modal handlers
  const openModal = (mode, dialer = null) => {
    setModalMode(mode)
    setCurrentDialer(dialer)
    // Include status_selector default when creating/cloning to ensure bot can read it
    const defaultStatusSelector = '#Tabs table tbody tr td:nth-child(5) img[src]'
    setFormData(dialer && (mode === 'edit' || mode === 'clone')
      ? {
          name: mode === 'clone' ? `${dialer.name} (Copy)` : dialer.name,
          is_archived: dialer.is_archived || false,
          status_selector: dialer.status_selector || defaultStatusSelector
        }
      : { name: '', is_archived: false, status_selector: defaultStatusSelector })
    setShowModal(true)
    setActiveMenu(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (modalMode === 'add' || modalMode === 'clone') await dialerApi.create(formData)
      else if (modalMode === 'edit') await dialerApi.update(currentDialer.id, formData)
      loadData()
    } catch (error) {
      console.error('Failed to save:', error)
    }
    setShowModal(false)
  }

  const handleDelete = async () => {
    try {
      await dialerApi.delete(currentDialer.id)
      loadData()
    } catch (error) {
      console.error('Failed to delete:', error)
    }
    setShowModal(false)
  }

  const handleArchive = async (dialer) => {
    try {
      await dialerApi.update(dialer.id, { ...dialer, is_archived: !dialer.is_archived })
      loadData()
    } catch (error) {
      console.error('Failed to archive:', error)
    }
    setActiveMenu(null)
  }

  const handleBulkDelete = async () => {
    if (!selectedDialers.length) return
    setBulkDeleting(true)
    try {
      await Promise.all(selectedDialers.map(id => dialerApi.delete(id)))
      setSelectedDialers([])
      setSelectAll(false)
      loadData()
    } catch (error) {
      console.error('Failed to bulk delete:', error)
    }
    setBulkDeleting(false)
    setShowBulkDeleteModal(false)
  }

  return (
    <div className="dialers-page">
      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className={`toast ${toast.type}`}
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
          >
            {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Dialers</h1>
          <p className="page-subtitle">
            Manage dialers and monitor bot activity
          </p>
        </div>
        <div className="page-header-actions">
          <button
            onClick={() => loadData(true)}
            className="btn btn-secondary btn-icon"
            disabled={refreshing}
          >
            <RefreshCw size={18} className={refreshing ? 'spinner' : ''} />
          </button>
          <button className="btn btn-primary" onClick={() => openModal('add')}>
            <Plus size={18} /> New Dialer
          </button>
        </div>
      </div>

      {/* Stats Blocks */}
      <div className="stat-blocks">
        <div className="stat-block green">
          <div className="stat-block-icon"><Play size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{botSummary.totals.active}</span>
            <span className="stat-block-label">Active Bots</span>
          </div>
        </div>
        <div className="stat-block orange">
          <div className="stat-block-icon"><Pause size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{botSummary.totals.paused || 0}</span>
            <span className="stat-block-label">Paused</span>
          </div>
        </div>
        <div className="stat-block cyan">
          <div className="stat-block-icon"><Clock size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{botSummary.totals.waiting || 0}</span>
            <span className="stat-block-label">Waiting</span>
          </div>
        </div>
        <div className="stat-block red">
          <div className="stat-block-icon"><AlertCircle size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{botSummary.totals.inactive_3d || 0}</span>
            <span className="stat-block-label">Offline 3+ Days</span>
          </div>
        </div>
        <div className="stat-block blue">
          <div className="stat-block-icon"><Bot size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{botSummary.totals.total}</span>
            <span className="stat-block-label">Total Bots</span>
          </div>
        </div>
        <div className="stat-block purple">
          <div className="stat-block-icon"><Phone size={20} /></div>
          <div className="stat-block-content">
            <span className="stat-block-value">{dialers.filter(d => !d.is_archived).length}</span>
            <span className="stat-block-label">Active Dialers</span>
          </div>
        </div>
      </div>

      {/* Alerts Section - Crashed Bots */}
      {crashedBots.length > 0 && (
        <div className="alerts-section">
          <div className="alert-box">
            <AlertTriangle size={18} className="alert-icon" />
            <div className="alert-content">
              <div className="alert-title">{crashedBots.length} bot{crashedBots.length !== 1 ? 's' : ''} need attention</div>
              <div className="alert-chips">
                {crashedBots.slice(0, 8).map((bot, idx) => (
                  <div
                    key={idx}
                    className="alert-chip"
                    onClick={() => {
                      const dialer = dialers.find(d => d.name === bot.dialer_name)
                      if (dialer) navigate(`/dialer-details/${dialer.id}`)
                    }}
                  >
                    <span className="sip-id">{bot.sip_id}</span>
                    <span className="dialer-name">{bot.dialer_name || 'Unknown'}</span>
                  </div>
                ))}
                {crashedBots.length > 8 && (
                  <span style={{ padding: '4px 10px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    +{crashedBots.length - 8} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <div className="search-wrapper">
            <Search size={14} className="search-icon" />
            <input
              type="text"
              placeholder="Search by ID, name, or client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          <button
            className={`toggle-btn ${showArchived ? 'active' : ''}`}
            onClick={() => setShowArchived(!showArchived)}
          >
            <Archive size={14} /> {showArchived ? 'Archived' : 'Active'}
          </button>
        </div>
        <div className="toolbar-right">
          {selectedDialers.length > 0 && (
            <button className="btn btn-danger" onClick={() => setShowBulkDeleteModal(true)}>
              <Trash2 size={14} /> Delete ({selectedDialers.length})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-scroll">
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
              <span>Loading dialers...</span>
            </div>
          ) : filteredDialers.length === 0 ? (
            <div className="empty-state">
              <Phone size={48} />
              <p>No dialers found. Try adjusting your filters or create a new dialer.</p>
            </div>
          ) : (
            <>
              <table className="table dialers-table">
                <thead>
                  <tr>
                    <th className="checkbox-cell">
                      <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                    </th>
                    <th>Dialer</th>
                    <th>Client</th>
                    <th className="th-avatar">Avatar</th>
                    <th className="th-icon" title="Active Bots"><Activity size={13} /></th>
                    <th className="th-icon" title="Waiting Bots"><Clock size={13} /></th>
                    <th className="th-icon" title="Offline Bots"><WifiOff size={13} /></th>
                    <th className="th-center th-total">Bots</th>
                    <th className="th-center">VMs</th>
                    <th className="th-center">Last Active</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th className="actions-col"></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDialers.map((dialer) => {
                    const counts = getBotCounts(dialer.id)
                    return (
                      <tr
                        key={dialer.id}
                        className={selectedDialers.includes(dialer.id) ? 'selected' : ''}
                        onClick={() => navigate(`/dialer-details/${dialer.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="checkbox-cell" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedDialers.includes(dialer.id)} onChange={() => handleSelect(dialer.id)} />
                        </td>
                        <td>
                          <div className="dialer-name-cell">
                            <div className="dialer-avatar">
                              {dialer.name?.charAt(0).toUpperCase() || 'D'}
                            </div>
                            <div>
                              <div className="dialer-name">{dialer.name}</div>
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>ID: {dialer.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="td-client">
                          {counts.client_name ? (
                            <span className="client-name">{counts.client_name}</span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                          )}
                        </td>
                        <td className="td-avatar">
                          {counts.avatar_name ? (
                            <span className="avatar-name" title={counts.avatar_name}>{counts.avatar_name}</span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                          )}
                        </td>
                        <td className="td-stat">
                          {counts.total > 0 ? (
                            <span className="stat-pill active" title="Active bots">
                              <Activity size={11} /> {counts.active}
                            </span>
                          ) : (
                            <span className="stat-pill empty">-</span>
                          )}
                        </td>
                        <td className="td-stat">
                          {counts.total > 0 ? (
                            <span className={`stat-pill ${counts.waiting > 0 ? 'waiting' : 'muted'}`} title="Waiting bots">
                              <Clock size={11} /> {counts.waiting || 0}
                            </span>
                          ) : (
                            <span className="stat-pill empty">-</span>
                          )}
                        </td>
                        <td className="td-stat">
                          {counts.total > 0 ? (
                            <span className={`stat-pill ${counts.offline > 0 ? 'offline' : 'muted'}`} title="Offline bots">
                              <WifiOff size={11} /> {counts.offline || 0}
                            </span>
                          ) : (
                            <span className="stat-pill empty">-</span>
                          )}
                        </td>
                        <td className="td-stat">
                          <span className="stat-pill redstone-total" title="Total bots">
                            <Users size={13} /> {counts.total}
                          </span>
                        </td>
                        <td className="td-stat">
                          {counts.total > 0 ? (
                            <span className={`stat-pill ${counts.linked_vms === counts.total ? 'linked' : counts.linked_vms > 0 ? 'partial' : 'unlinked'}`} title="Linked VMs">
                              {counts.linked_vms}/{counts.total}
                            </span>
                          ) : (
                            <span className="stat-pill empty">-</span>
                          )}
                        </td>
                        <td className="td-time">
                          {counts.last_heartbeat ? (
                            <span className="time-ago" title={new Date(counts.last_heartbeat).toLocaleString()}>
                              {formatTimeAgo(counts.last_heartbeat)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>-</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {counts.total === 0 ? (
                            <span className="status-badge no-bots">No Bots</span>
                          ) : counts.active > 0 && counts.active === counts.total ? (
                            <span className="status-badge online">Active</span>
                          ) : counts.active > 0 ? (
                            <span className="status-badge partial">Partial</span>
                          ) : (
                            <span className="status-badge offline">Offline</span>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="actions-cell">
                            <button
                              className="action-btn view"
                              onClick={() => navigate(`/dialer-details/${dialer.id}`)}
                              title="View Details"
                            >
                              <ExternalLink size={14} />
                            </button>
                            <button
                              className="action-btn"
                              onClick={() => openModal('edit', dialer)}
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              className="action-btn danger"
                              onClick={() => openModal('delete', dialer)}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <span className="pagination-info">
          Showing {Math.min((currentPage - 1) * pageSize + 1, filteredDialers.length)} - {Math.min(currentPage * pageSize, filteredDialers.length)} of {filteredDialers.length}
        </span>
        <div className="pagination-controls">
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="pagination-select"
          >
            {PAGE_SIZES.map(size => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
          <button
            className="pagination-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
          >
            <ChevronLeft size={14} />
          </button>
          <span className="pagination-pages">
            {currentPage} / {totalPages || 1}
          </span>
          <button
            className="pagination-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          >
            <motion.div
              className="modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '440px', boxShadow: 'var(--shadow-xl)' }}
            >
              <div style={{ padding: 'var(--space-lg)', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                  {modalMode === 'add' && 'Create New Dialer'}
                  {modalMode === 'edit' && 'Edit Dialer'}
                  {modalMode === 'clone' && 'Clone Dialer'}
                  {modalMode === 'delete' && 'Delete Dialer'}
                </h2>
                <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px' }}>
                  <X size={20} />
                </button>
              </div>
              {modalMode === 'delete' ? (
                <div style={{ padding: 'var(--space-lg)' }}>
                  <p style={{ margin: 0 }}>Are you sure you want to delete <strong>{currentDialer?.name}</strong>?</p>
                  <p style={{ margin: '12px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>This action cannot be undone.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={{ padding: 'var(--space-lg)' }}>
                    <div style={{ marginBottom: 'var(--space-md)' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '8px', color: 'var(--text-secondary)' }}>Dialer Name</label>
                      <input
                        type="text"
                        placeholder="Enter dialer name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={formData.is_archived} onChange={(e) => setFormData({ ...formData, is_archived: e.target.checked })} />
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Archive this dialer</span>
                    </label>
                  </div>
                </form>
              )}
              <div style={{ padding: 'var(--space-md) var(--space-lg)', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)', background: 'var(--bg-tertiary)' }}>
                <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                {modalMode === 'delete' ? (
                  <button className="btn btn-danger" onClick={handleDelete}><Trash2 size={16} /> Delete</button>
                ) : (
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    <Check size={16} />
                    {modalMode === 'add' ? 'Create' : modalMode === 'edit' ? 'Save' : 'Clone'}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBulkDeleteModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '400px', boxShadow: 'var(--shadow-xl)' }}
            >
              <div style={{ padding: 'var(--space-lg)', borderBottom: '1px solid var(--border-color)' }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertTriangle size={20} style={{ color: 'var(--danger)' }} /> Bulk Delete
                </h2>
              </div>
              <div style={{ padding: 'var(--space-lg)' }}>
                <p style={{ margin: 0 }}>Delete <strong>{selectedDialers.length}</strong> selected dialer{selectedDialers.length !== 1 ? 's' : ''}?</p>
                <p style={{ margin: '12px 0 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>This action cannot be undone.</p>
              </div>
              <div style={{ padding: 'var(--space-md) var(--space-lg)', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)', background: 'var(--bg-tertiary)' }}>
                <button className="btn btn-ghost" onClick={() => setShowBulkDeleteModal(false)} disabled={bulkDeleting}>Cancel</button>
                <button className="btn btn-danger" onClick={handleBulkDelete} disabled={bulkDeleting}>
                  {bulkDeleting ? <><RefreshCw size={16} className="spin" /> Deleting...</> : <><Trash2 size={16} /> Delete</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  )
}

export default Dialers
